/**
 * SVG Editor Component
 * Visual editor for modifying SVG properties
 */

import { memo, useState, useCallback, useRef } from 'react';
import { useToast } from '../../context';
import {
  parseSvg,
  applySvgChanges,
  getEditableProperties,
  isColorValue,
  colorToHex,
  formatSvg,
  type SvgElement,
  type SvgDocument,
} from '../../services/svgEditorService';

// Icons
const UploadIcon = memo(() => (
  <svg className="w-12 h-12 mx-auto mb-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
));
UploadIcon.displayName = 'UploadIcon';

const DownloadIcon = memo(() => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
));
DownloadIcon.displayName = 'DownloadIcon';

const CopyIcon = memo(() => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
));
CopyIcon.displayName = 'CopyIcon';

const SpinnerIcon = memo(() => (
  <svg className="w-8 h-8 animate-spin text-indigo-400" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
));
SpinnerIcon.displayName = 'SpinnerIcon';

export const SvgEditor = memo(function SvgEditor() {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [svgDoc, setSvgDoc] = useState<SvgDocument | null>(null);
  const [currentSvg, setCurrentSvg] = useState<string>('');
  const [selectedElement, setSelectedElement] = useState<SvgElement | null>(null);
  const [fileName, setFileName] = useState('edited.svg');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { success, error, info } = useToast();

  const processFile = useCallback(async (file: File) => {
    if (!file.type.includes('svg')) {
      error('Please upload an SVG file');
      return;
    }

    setIsProcessing(true);
    info('Loading SVG...');

    try {
      const text = await file.text();
      const parsed = parseSvg(text);
      
      if (!parsed) {
        error('Failed to parse SVG');
        return;
      }

      setSvgDoc(parsed);
      setCurrentSvg(text);
      setFileName(file.name);
      setSelectedElement(null);
      success('SVG loaded!');
    } catch (err) {
      error('Failed to load SVG');
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  }, [success, error, info]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.includes('<svg')) {
        const parsed = parseSvg(text);
        if (parsed) {
          setSvgDoc(parsed);
          setCurrentSvg(text);
          setFileName('pasted.svg');
          setSelectedElement(null);
          success('SVG pasted!');
        } else {
          error('Invalid SVG code');
        }
      } else {
        error('Clipboard does not contain SVG');
      }
    } catch {
      error('Failed to read clipboard');
    }
  }, [success, error]);

  const handleElementSelect = useCallback((element: SvgElement) => {
    setSelectedElement(element);
  }, []);

  const handlePropertyChange = useCallback((property: string, value: string) => {
    if (!selectedElement || !svgDoc) return;

    const newSvg = applySvgChanges(currentSvg, selectedElement.path, { [property]: value });
    setCurrentSvg(newSvg);

    // Update the element in state
    const newElements = svgDoc.elements.map(el => {
      if (el.id === selectedElement.id) {
        return {
          ...el,
          attributes: { ...el.attributes, [property]: value },
        };
      }
      return el;
    });

    setSvgDoc({ ...svgDoc, elements: newElements });
    setSelectedElement({ ...selectedElement, attributes: { ...selectedElement.attributes, [property]: value } });
  }, [selectedElement, svgDoc, currentSvg]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([formatSvg(currentSvg)], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    success('SVG downloaded!');
  }, [currentSvg, fileName, success]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(formatSvg(currentSvg));
      success('SVG copied to clipboard!');
    } catch {
      error('Failed to copy');
    }
  }, [currentSvg, success, error]);

  const handleClear = useCallback(() => {
    setSvgDoc(null);
    setCurrentSvg('');
    setSelectedElement(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const editableProps = selectedElement ? getEditableProperties(selectedElement.tagName) : [];

  return (
    <div className="space-y-6">
      {!svgDoc ? (
        <>
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`
              relative rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer
              ${isDragging ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-600 hover:border-indigo-400 bg-slate-800/50'}
              ${isProcessing ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".svg,image/svg+xml"
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={isProcessing}
            />
            
            {isProcessing ? (
              <div className="py-4">
                <SpinnerIcon />
                <p className="mt-4 text-slate-400">Loading SVG...</p>
              </div>
            ) : (
              <>
                <UploadIcon />
                <p className="text-slate-300 font-medium">Drop your SVG file here</p>
                <p className="text-slate-500 text-sm mt-1">or click to browse</p>
              </>
            )}
          </div>

          {/* Paste Button */}
          <div className="text-center">
            <button
              onClick={handlePaste}
              className="px-6 py-2.5 text-sm font-medium bg-slate-700 hover:bg-slate-600 text-white rounded-lg border border-slate-600 transition-all"
            >
              Or Paste SVG Code from Clipboard
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Editor Layout */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Preview Panel */}
            <div className="lg:col-span-2 bg-slate-800 rounded-xl border border-slate-700 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-300">Preview</h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                  >
                    <CopyIcon />
                    Copy
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors"
                  >
                    <DownloadIcon />
                    Download
                  </button>
                </div>
              </div>
              
              <div className="bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMzMzIi8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMzMzMiLz48L3N2Zz4=')] rounded-lg p-4 flex items-center justify-center min-h-[300px]">
                <div 
                  dangerouslySetInnerHTML={{ __html: currentSvg }}
                  className="max-w-full max-h-[400px] [&>svg]:max-w-full [&>svg]:max-h-[400px]"
                />
              </div>
            </div>

            {/* Elements Panel */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
              <h3 className="text-sm font-medium text-slate-300 mb-4">Elements</h3>
              <div className="space-y-1 max-h-[350px] overflow-y-auto">
                {svgDoc.elements.map((element) => (
                  <button
                    key={element.id}
                    onClick={() => handleElementSelect(element)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedElement?.id === element.id
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    <span className="font-mono">&lt;{element.tagName}&gt;</span>
                    {element.attributes.id && (
                      <span className="text-slate-400 ml-2">#{element.attributes.id}</span>
                    )}
                    {element.attributes.class && (
                      <span className="text-slate-500 ml-1 text-xs">.{element.attributes.class}</span>
                    )}
                  </button>
                ))}
                {svgDoc.elements.length === 0 && (
                  <p className="text-slate-500 text-sm">No editable elements found</p>
                )}
              </div>
            </div>
          </div>

          {/* Properties Panel */}
          {selectedElement && (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
              <h3 className="text-sm font-medium text-slate-300 mb-4">
                Properties: <span className="text-indigo-400">&lt;{selectedElement.tagName}&gt;</span>
              </h3>
              
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                {editableProps.map((prop) => {
                  const value = selectedElement.attributes[prop] || '';
                  const isColor = isColorValue(value) || prop === 'fill' || prop === 'stroke';
                  
                  return (
                    <div key={prop}>
                      <label className="block text-xs text-slate-400 mb-1.5">{prop}</label>
                      <div className="flex gap-2">
                        {isColor && value !== 'none' && (
                          <input
                            type="color"
                            value={colorToHex(value || '#000000')}
                            onChange={(e) => handlePropertyChange(prop, e.target.value)}
                            className="w-10 h-9 rounded cursor-pointer border-0 bg-transparent"
                          />
                        )}
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => handlePropertyChange(prop, e.target.value)}
                          placeholder={prop}
                          className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Quick Actions */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <h4 className="text-xs text-slate-400 mb-2">Quick Actions</h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handlePropertyChange('fill', 'none')}
                    className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                  >
                    No Fill
                  </button>
                  <button
                    onClick={() => handlePropertyChange('stroke', 'none')}
                    className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                  >
                    No Stroke
                  </button>
                  <button
                    onClick={() => handlePropertyChange('opacity', '0.5')}
                    className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                  >
                    50% Opacity
                  </button>
                  <button
                    onClick={() => handlePropertyChange('stroke-width', '2')}
                    className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                  >
                    Stroke: 2
                  </button>
                  <button
                    onClick={() => handlePropertyChange('transform', 'rotate(45)')}
                    className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                  >
                    Rotate 45°
                  </button>
                  <button
                    onClick={() => handlePropertyChange('transform', 'scale(1.5)')}
                    className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                  >
                    Scale 1.5x
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Clear Button */}
          <div className="text-center">
            <button
              onClick={handleClear}
              className="px-6 py-2.5 text-sm font-medium bg-slate-700 hover:bg-slate-600 text-white rounded-lg border border-slate-600 hover:border-slate-500 transition-all"
            >
              Edit Another SVG
            </button>
          </div>
        </>
      )}

      {/* Info */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <h4 className="text-sm font-medium text-slate-300 mb-2">🎨 SVG Editor Features:</h4>
        <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
          <li>Click on elements in the list to select and edit them</li>
          <li>Change colors, stroke widths, opacity, and transforms</li>
          <li>Use color pickers for fill and stroke properties</li>
          <li>Quick action buttons for common modifications</li>
          <li>Download or copy the edited SVG code</li>
        </ul>
      </div>
    </div>
  );
});

export default SvgEditor;
